import React, { Fragment, MouseEventHandler, useCallback, useState } from 'react'
import clsx from 'clsx'
import Drawer from '@mui/material/Drawer'
import PersonIcon from '@mui/icons-material/Person'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PublicIcon from '@mui/icons-material/Public'
import HomeIcon from '@mui/icons-material/Home'
import Typography from '@mui/material/Typography'
import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import Collapse from '@mui/material/Collapse'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import NotificationsIcon from '@mui/icons-material/Notifications'
import Badge from '@mui/material/Badge'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

import { FormDialog } from 'duck/AppDuck/DialogDuck'
import { MAIN_PAGE, NOTIFICATION, route } from 'routes'
import Button from 'components/Button'
import { NavbarMobileProps } from './'
import LogoWrapper from './LogoWrapper'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './NavBarGeneral.module.scss'

const classesNavMobileDrawer = {
  paper: clsx('flex flex-col bg-light', styles.NavMobileDrawerPaper)
}

const classesButton = {
  startIcon: 'ml-0 mr-2.5'
}

const classesBadge = {
  badge: clsx('h-6 min-w-6 px-0.5 py-0', styles.BadgeInner)
}

type NavRouteType = {
  child?: boolean
  hide?: boolean
  icon?: any
  links?: any
  onClick: MouseEventHandler
  title: string
  to: string
}

const ButtonComponent: React.FC<NavRouteType> = props => {
  const { child, icon: IconComponent, links, onClick, title, to } = props
  const [open, setOpen] = useState(false)

  return (
    <div>
      <div>
        <Button
          className={clsx('mb-px px-4 py-2 uppercase', child && 'text-white-med-on-dark')}
          to={to}
          classes={classesButton}
          onClick={
            links
              ? () => {
                  setOpen(prev => !prev)
                }
              : onClick
          }
          endIcon={
            links ? (
              <KeyboardArrowDownIcon className={clsx('!text-xl', open && 'rotate-180')} />
            ) : undefined
          }
          startIcon={IconComponent ? <IconComponent className="!text-base" /> : undefined}
          align="left"
          fullWidth
        >
          {child ? <span className="pl-6.5"></span> : null}
          <Typography className="w-1/100 min-w-0 flex-auto" variant="button">
            {title}
          </Typography>
        </Button>
      </div>

      {links ? (
        <Collapse in={open}>
          <div>
            {links.map((itemChild: any, index: number) => {
              return (
                <ButtonComponent
                  key={index}
                  to={itemChild.to}
                  title={itemChild.title}
                  onClick={onClick}
                  child
                />
              )
            })}
          </div>
        </Collapse>
      ) : undefined}
    </div>
  )
}

const NavBarMobile: React.FC<NavbarMobileProps> = props => {
  const {
    activeMenu,
    isOpen,
    loading,
    openDialog,
    showMainMenu,
    userName,
    isAuthorized,
    unreadCount,
    userAlias,
    setOpen,
    setActiveMenu
  } = props

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
    setActiveMenu('')
  }, [setActiveMenu, setOpen])

  const handleClickDrawer = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return
      }

      if (activeMenu === NOTIFICATION) setActiveMenu('')
      else setOpen((prev?: boolean) => !prev)
    },
    [activeMenu, setActiveMenu, setOpen]
  )

  const NavRouteList = [
    {
      icon: HomeIcon,
      title: 'HOME',
      to: MAIN_PAGE,
      hide: !isAuthorized
    },
    {
      icon: PublicIcon,
      title: 'EXPLORE',
      to: route.EXPLORE.getUrl(),
      hide: isAuthorized
    },
    {
      icon: PublicIcon,
      title: 'EXPLORE',
      hide: !isAuthorized,
      links: [
        {
          title: 'FEATURED',
          to: route.EXPLORE.getUrl()
        },
        {
          title: 'POSTS',
          to: route.EXPLORE.getUrl({ subRoute: 'posts' })
        },
        // {
        //   title: 'FILTERS',
        //   to: route.EXPLORE.getUrl({ subRoute: 'filters' })
        // },
        {
          title: 'COLLECTIONS',
          to: route.EXPLORE.getUrl({ subRoute: 'collections' })
        },
        {
          title: 'PROJECTS',
          to: route.EXPLORE.getUrl({ subRoute: 'projects' })
        }
      ]
    },
    {
      icon: PersonIcon,
      title: userName || '',
      to: route.ARTISTS.getUrl({ artistUrl: userAlias }),
      hide: !isAuthorized
    },
    {
      icon: PersonOutlineIcon,
      title: 'ACCOUNT',
      to: route.PROFILE.getUrl(),
      hide: !isAuthorized
    },
    {
      icon: HelpOutlineIcon,
      title: 'HELP',
      onClick: () => {
        openDialog({ [FormDialog.SEND_QUESTION]: { dialogName: FormDialog.SEND_QUESTION } })
      },
      hide: !isAuthorized
    },
    {
      icon: PersonIcon,
      title: 'LOGIN',
      to: route.SIGN_IN.getUrl(),
      hide: isAuthorized
    }
  ]

  return (
    <Fragment key="NavBarMobile">
      <LogoWrapper onClick={handleCloseDrawer} />

      {showMainMenu && !loading ? (
        <>
          <Button
            className={clsx('rounded-none px-6', styles.NavMobileDrawerToggle)}
            onClick={handleClickDrawer}
            icon
          >
            {!isOpen ? (
              <>
                {unreadCount ? <span className={styles.RedDot} /> : null}
                <MenuIcon />
              </>
            ) : (
              <CloseIcon />
            )}
          </Button>

          <Drawer
            className={styles.NavMobileDrawer}
            anchor="right"
            open={isOpen}
            onClose={handleCloseDrawer}
            classes={classesNavMobileDrawer}
          >
            <div
              className={clsx('h-1/100 min-h-0 flex-auto pb-6', styles.NavMobileDrawerContent)}
              role="presentation"
            >
              {NavRouteList.map((item, index) => {
                if (item.hide) return null

                return (
                  <ButtonComponent
                    key={index}
                    to={item.to}
                    title={item.title}
                    icon={item.icon}
                    links={item.links}
                    onClick={
                      item.onClick
                        ? () => {
                            item.onClick()
                            handleCloseDrawer()
                          }
                        : handleCloseDrawer
                    }
                  />
                )
              })}
            </div>

            {isAuthorized ? (
              <div className={stylesGlobal.BorderTopOnSurface}>
                <Button
                  align="left"
                  onClick={() => {
                    setActiveMenu(NOTIFICATION)
                  }}
                  startIcon={<NotificationsIcon className="!text-lg" />}
                  fullWidth
                >
                  <Typography variant="button" className="font-bold">
                    NOTIFICATIONS
                  </Typography>

                  {!unreadCount ? (
                    <Badge
                      badgeContent={
                        <Typography variant="button" component="span">
                          {unreadCount || 2}
                        </Typography>
                      }
                      color="error"
                      className="absolute right-0 mr-6"
                      classes={classesBadge}
                    />
                  ) : null}
                </Button>
              </div>
            ) : null}
          </Drawer>
        </>
      ) : null}
    </Fragment>
  )
}

export default NavBarMobile
